import type { ComponentProps, ReactNode } from 'react';
import { cx } from './cx';
import { Spinner } from './Spinner';
import { base, buttonSizes, buttonVariants } from './button-styles';

export type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'warning' | 'ghost' | 'inverse';
export type ButtonSize = 'sm' | 'md';

export interface ButtonProps extends ComponentProps<'button'> {
  variant?: ButtonVariant | undefined;
  size?: ButtonSize | undefined;
  /** Shows a spinner, disables the button and sets aria-busy; the label stays for AT. */
  loading?: boolean | undefined;
  iconStart?: ReactNode;
  iconEnd?: ReactNode;
  fullWidth?: boolean | undefined;
}

/**
 * Button. Native <button> (keyboard + AT for free), type="button" by default so it never submits
 * a form by accident. Disabled uses the native attribute; icon-only buttons need `aria-label`.
 */
export function Button({
  variant = 'primary',
  size = 'md',
  loading = false,
  iconStart,
  iconEnd,
  fullWidth,
  disabled,
  className,
  children,
  type = 'button',
  ...props
}: ButtonProps) {
  return (
    <button
      type={type}
      disabled={(disabled ?? false) || loading}
      aria-busy={loading ? true : undefined}
      className={cx(
        base,
        buttonVariants[variant],
        buttonSizes[size],
        fullWidth && 'w-full',
        className,
      )}
      {...props}
    >
      {loading ? (
        <Spinner decorative size="sm" className="text-current" />
      ) : (
        iconStart && (
          <span aria-hidden="true" className="inline-flex">
            {iconStart}
          </span>
        )
      )}
      {children}
      {iconEnd && (
        <span aria-hidden="true" className="inline-flex">
          {iconEnd}
        </span>
      )}
    </button>
  );
}
